import * as vue from "vue";
import { onUnmounted, onMounted, ref } from "vue";

/**
 * Converts camel case (e.g. myNewVariable) to kebab
 * case (e.g. my-new-variable).
 *
 * @param {string} name
 * @returns {string}
 */
export function camelToKebabCase(name) {
    let newName = "";
    let upperCaseStarts = "A".charCodeAt(0);
    let upperCaseEnds = "Z".charCodeAt(0);
    for (let char of name) {
        let charCode = char.charCodeAt(0);

        if (charCode >= upperCaseStarts && charCode <= upperCaseEnds) {
            newName += "-"
            newName += char.toLowerCase();
        } else {
            newName += char;
        }
    }

    return newName;
}

/**
 * Converts a javascript variable name (in camel case) to
 * the equivalent css variable name
 * @param {string} name
 */
export function jsToCssVariable(name) {
    return "--" + camelToKebabCase(name);
}

/**
 * Make it so that all calls to this will stop until only one call
 * to the action is completed. The lock will be reset after a delay.
 * @template T
 * @param {() => Promise<T>} action Any kind of action that should only happen once in concurrent context
 * @param {number} resetDelay Minimum time before executing the action again on the next call
 * @returns {() => Promise<T>}
 */
export function locked(action, resetDelay) {
  let lockPromise = null;
  // Avoids running the setTimeout multiple times, which could 
  // incur into race conditions.
  let timeoutHandle = null;

  return async () => {
    if (lockPromise == null) {
      lockPromise = action();
    }

    let res = await lockPromise;

    if (timeoutHandle == null) {
      timeoutHandle = setTimeout(() => {
        lockPromise = null;
        timeoutHandle = null;
      }, resetDelay);
    }

    return res;
  }
}

/*
 * Makes sure that a promise is cancelled on component onmount
 * @returns {vue.Ref<boolean>}
 */
export function useIsMounted() {
  let isMounted = ref(true);

  vue.onUnmounted(() => isMounted.value = false);

  return isMounted;
}

/**
 * Converts a date string into another string, but formatted based on the locale
 * @param {string} dateStr
 * @returns {string}
 */
export function fmtDate(dateStr) {
  return new Date(dateStr).toLocaleDateString();
}

/**
 * Converts a datetime string into another string, but formatted based on the locale
 * @param {string} dateStr
 * @returns {string}
 */
export function fmtDateTime(dateStr) {
  return new Date(dateStr).toLocaleString();
}

/**
 * Convert an amount of bytes into a string with a more human-friendly representation
 * @param {number} bytes
 * @returns {string}
 */
export function fmtSize(bytes) {
  let kilobytes = bytes / 1024;
  let megabytes = kilobytes / 1024;
  let gigabytes = megabytes / 1024;

  if (gigabytes > 1)
    return gigabytes.toFixed(2) + " GB";

  if (megabytes > 1)
    return megabytes.toFixed(2) + " MB";

  return kilobytes.toFixed(2) + " KB";
}

/**
 * Triggers the browser download for a blob of data
 * @param {Blob} blob
 * @param {string} name
 */
export function download(blob, name) {
  var fileUrl = window.URL.createObjectURL(blob);
  var fileLink = document.createElement('a');
  fileLink.href = fileUrl;
  fileLink.setAttribute('download', name);
  document.body.appendChild(fileLink);
  fileLink.click();
  document.body.removeChild(fileLink);
}

/**
 * Creates an interval that triggers a callback at a specified delay. It
 * automatically starts and stops the interval on mount/unmount.
 * @param {() => void} cb 
 * @param {number} delay 
 * @returns {vue.Ref<number | null>}
 */
export function useIntervalFn(cb, delay) {
  const interval = ref(null);

  onMounted(() => {
    interval.value = setInterval(cb, delay);
  });

  onUnmounted(() => {
    clearInterval(interval.value);
  });

  return interval;
}
